import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import checkmark from "../../assets/checkmark.svg";
import Sidebanner from "../businesspages/sidebanner";
import { useAuth } from "../../context/AuthContext"; 
import supabase from "../../supabase";
import { useNavigate } from "react-router-dom";

export default function Pricing({ isBusiness }) {
  const { user, business, subscription, loading: authLoading } = useAuth();
  
  const navigate = useNavigate();
  const [upgrading, setUpgrading] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [targetPrice, setTargetPrice] = useState(null);

  useEffect(() => {
    if (window.Paddle) {
      window.Paddle.Initialize({ token: "live_1be4e25ebd1c23045f0ba001008"});
    }
  }, []);

  // --- LOGIC HELPERS ---
  const isBeingCanceled = subscription?.is_being_canceled;
  const isTrial = subscription?.plan_type === 'free_trial' && subscription?.status === 'trialing';
  const status = subscription?.status;
  const plan = subscription?.plan_type;

  const formatDate = (dateString) => {
    if (!dateString) return null;
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short', day: 'numeric', year: 'numeric'
    });
  };

  const handlePlanSelection = (priceId) => {
    if (upgrading || cancelling || isBeingCanceled) return;

    // Check for Standard -> Premium Upgrade
    const isPaidStandard = plan === 'standard' && (status === 'active' || status === 'paid');
    const isUpgradingToPremium = priceId === "pri_01kmam8t754jbv7jdth0nq0yg5";

    if (isPaidStandard && isUpgradingToPremium && subscription?.paddle_subscription_id) {
      setTargetPrice(priceId);
      setShowUpgradeModal(true);
      return;
    }

    if (!business?.id) {
      navigate('/business/signup', { state: { plan: priceId } });
      return;
    }

    if (!window.Paddle) return;

    window.Paddle.Checkout.open({
      settings: { displayMode: "overlay", theme: "light" },
      customer: { email: user.email },
      customData: { supabase_business_id: business.id },
      items: [{ priceId, quantity: 1 }],
    });
  };

  const confirmCancellation = async () => {
    if (cancelling || upgrading) return;
    const subId = subscription?.paddle_subscription_id;
    setCancelling(true);
    try {
      const { error } = await supabase.functions.invoke("cancel-subscription", {
        body: { subscriptionId: subId }
      });
      if (error) throw error;
      await supabase.from('subscriptions').update({ 
        is_being_canceled: true,
        deletion_date: subscription?.next_bill_date 
      }).eq('paddle_subscription_id', subId);
      window.location.reload();
    } catch (err) {
      alert(err.message || "Cancellation failed.");
      setCancelling(false);
      setShowCancelModal(false);
    }
  };

  const confirmUpgrade = async () => {
    if (upgrading) return;
    setUpgrading(true);
    try {
      const { error } = await supabase.functions.invoke("update-subscription", {
        body: { subscriptionId: subscription?.paddle_subscription_id, newPriceId: targetPrice }
      });
      if (error) throw error;
      window.location.href = `/business/home?upgrade=success`;
    } catch (err) {
      alert(err.message || "Upgrade failed.");
      setUpgrading(false);
    }
  };

  const newModules = [
    {
      title: 'Automated Invitations',
      context: 'Scale your reach with your plan’s automated invitations every month.',
      content: 'Keep your profile active without the manual work. Simply upload your customer list and let our system handle the rest.'
    },
    {
      title: 'Screenshot To Review Conversion',
      context: 'Transform raw customer interactions into bulletproof social proof.',
      content: 'Our Business Verified Conversation engine captures the authenticity of your real customer chats and turns them into high-conversion trust assets.'
    },
    {
      title: 'Manifest Widgets',
      context: 'Display your verified proof with high-conversion, dynamic widgets.',
      content: "Seamlessly integrate with your website to showcase your reviews in real-time. Designed for speed and trust."
    },
    {
      title: 'Anti-Spam Shield',
      context: 'Protect your brand with verified-access profile logic.',
      content: 'Our Anti-Spam Shield ensures your profile is accessible to real customers while remaining invisible to bots.'
    }
  ];

  const PricingCard = ({ title, price, desc, features, colorClass, buttonText, onClick, isCurrentPlan, disabled, isTrialMode }) => {
    const displayDate = formatDate(subscription?.deletion_date || subscription?.next_bill_date);

    return (
      <div className={`w-full ${colorClass} max-w-[350px] lg:max-w-[380px] xl:max-w-[420px] 2xl:max-w-[450px] rounded-[12px] relative h-[620px] pt-6 px-8 shadow-xl transition-transform hover:scale-[1.02]`}>
        <div className='flex flex-col w-full gap-3'>
          <span className='text-[24px] font-staat text-white tracking-[1px]'>{title}</span>
          <div className='flex flex-row w-full gap-4 items-end'>
            <span className='font-tenali text-[42px] leading-none text-white tracking-[1px]'>${price}</span>
            <span className='font-poppins text-[12px] text-white opacity-80 mb-1'>per month</span>
          </div>
          <p className='font-poppins text-[12px] text-white min-h-[40px] opacity-90'>{desc}</p>
          <div className='w-full h-[1px] bg-white opacity-20'></div>
        </div>

        <div className='flex flex-col w-full gap-4 mt-8'>
          {features.map((f, i) => (
            <div key={i} className='flex flex-row gap-4 items-center'>
              <div className='w-5 h-5 rounded-full bg-[#1E7D6B]/40 flex items-center justify-center flex-shrink-0'>
                <img src={checkmark} alt="" className="w-3 h-3 brightness-200" />
              </div>
              <p className='text-[11px] xl:text-[13px] font-poppins text-white tracking-[1px]'>{f}</p>
            </div>
          ))}
        </div>

        <div className="absolute bottom-8 left-0 w-full flex flex-col items-center gap-3">
          <button 
            onClick={onClick}
            disabled={authLoading || (isCurrentPlan && !isBeingCanceled) || disabled || upgrading || cancelling || isBeingCanceled}
            className='w-[80%] h-[50px] border-2 border-white rounded-md text-[12px] font-bold font-poppins tracking-[2px] text-white hover:bg-white hover:text-[#32596E] transition-all uppercase disabled:opacity-30'
          >
            {authLoading ? '...' : isCurrentPlan ? (isBeingCanceled ? 'Ending Soon' : 'Current Plan') : buttonText}
          </button>

          {/* THE FIX: Wrap status check in parentheses to handle active OR paid correctly */}
          {isCurrentPlan && (status === 'active' || status === 'paid') && !isBeingCanceled && (
             <button 
                onClick={(e) => {
                  e.stopPropagation();
                  setShowCancelModal(true);
                }} 
                className="text-[9px] text-white/50 hover:text-white font-bold uppercase tracking-[1px] underline underline-offset-4"
             >
                Cancel Subscription
             </button>
          )}

          {isCurrentPlan && isTrialMode && (
             <p className="text-[9px] text-white/60 font-bold uppercase tracking-[1px]">Free Trial Ends: {displayDate}</p>
          )}

          {isCurrentPlan && isBeingCanceled && (
             <p className="text-[9px] text-orange-300 font-bold uppercase tracking-[1px]">Ends {displayDate}</p>
          )}
        </div>
      </div>
    );
  };

  const commonContent = (
    <div className={`h-auto pt-[80px] lg:pt-[120px] pb-20 bg-[#F3F3F3] w-full min-h-screen`}>
      <Helmet>
        <title>Pricing | Sitemanifest</title>
        <link rel="canonical" href="https://sitemanifest.com/pricing" />
      </Helmet>

      <div className='flex flex-col items-center gap-4 mb-12'>
        <h1 className='font-poppins text-[14px] tracking-[4px] uppercase text-gray-500'>Plans & Pricing</h1>
        <h2 className='font-staat text-[32px] 2xl:text-[52px] tracking-[4px] text-center text-[#323232]'>Choose Your Plan</h2>
      </div>

      <div className='flex flex-col lg:flex-row justify-center items-center gap-10 px-6'>
        <PricingCard 
          title="Standard Plan" price="199" colorClass="bg-[#3EBAA2]" desc="Showcase full reviews in your marketing." buttonText="Select Standard"
          isCurrentPlan={plan === 'standard' && (status === 'active' || status === 'paid')} 
          isTrialMode={isTrial && plan === 'standard'}
          disabled={plan === 'premium' && (status === 'active' || status === 'paid') && !isBeingCanceled}
          onClick={() => handlePlanSelection('pri_01kmam7r662w84b6wgfdekgask')} 
          features={["Screenshot To Review Conversion", "500 Monthly Invitations", "Manifest Widgets", "Anti-Spam Shield", "Intel Reports"]} 
        />
        <PricingCard 
          title="Premium Plan" price="399" colorClass="bg-[#32596E]" desc="Maximize trust and visibility for scaling businesses." buttonText="Select Premium"
          isCurrentPlan={plan === 'premium' && (status === 'active' || status === 'paid')}
          isTrialMode={isTrial && plan === 'premium'}
          onClick={() => handlePlanSelection('pri_01kmam8t754jbv7jdth0nq0yg5')} 
          features={["Screenshot To Review Conversion", "1,500 Monthly Invitations", "Manifest Widgets", "Anti-Spam Shield", "Priority Intel Reports", "Premium Badge"]} 
        />
      </div>

      {isBeingCanceled && (
        <p className="text-center mt-10 text-[11px] font-bold text-gray-400 uppercase tracking-widest px-6">
          You need to wait for the next cycle to renew or buy new plans
        </p>
      )}

      <div className='max-w-[1080px] mx-auto mt-20 px-6'>
        <div className='flex flex-col gap-10'>
          {newModules.map((item, index) => (
            <div key={index} className='border-b border-gray-300 pb-8'>
              <div className='flex flex-col lg:flex-row lg:justify-between lg:items-end gap-2 mb-4'>
                <h3 className='text-[24px] font-staat tracking-[1px] text-black'>{item.title}</h3>
                <span className='text-[12px] font-poppins text-gray-500 italic'>{item.context}</span>
              </div>
              <p className='text-[14px] leading-relaxed font-poppins text-gray-700'>{item.content}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  return (
    <div className="flex w-full">
      {!isBusiness && <div className="w-full">{commonContent}</div>}
      {isBusiness && (
        <>
          <div className="flex-1 lg:pr-[250px] xl:pr-[320px]">{commonContent}</div>
          <Sidebanner />
        </>
      )}

      {/* --- MODAL: UPGRADE --- */}
      {showUpgradeModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-[12px] p-10 max-w-sm w-full text-center shadow-2xl border border-gray-100">
            <h3 className="font-staat text-4xl text-[#32596E] mb-2 uppercase">Instant Upgrade</h3>
            <div className="bg-gray-50 rounded-xl p-6 mb-6 text-center border border-gray-100">
              <p className="text-[#32596E] text-[10px] font-black uppercase tracking-widest mb-1">Difference to Pay Now</p>
              <p className="text-4xl font-black text-[#32596E] tracking-tighter">$100.00</p>
            </div>
            <div className="flex flex-col gap-3">
              <button onClick={confirmUpgrade} disabled={upgrading} className="w-full bg-[#32596E] text-white py-4 rounded-md font-bold uppercase tracking-widest text-[11px]">
                {upgrading ? "Processing..." : "Confirm & Unlock Premium"}
              </button>
              <button onClick={() => !upgrading && setShowUpgradeModal(false)} className="text-gray-400 text-[10px] font-bold uppercase tracking-widest py-2">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* --- MODAL: CANCELLATION --- */}
      {showCancelModal && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-md z-[9999] flex items-center justify-center p-4">
          <div className="bg-white rounded-[12px] p-10 max-w-sm w-full text-center shadow-2xl border border-gray-100">
            <h3 className="font-staat text-4xl text-[#323232] mb-2 uppercase">Stop Subscription?</h3>
            <p className="text-[11px] text-gray-500 mb-6 px-4">You will keep your features until <strong>{formatDate(subscription?.next_bill_date)}</strong>.</p>
            <div className="flex flex-col gap-3">
              <button onClick={confirmCancellation} disabled={cancelling} className="w-full bg-red-500 text-white py-4 rounded-md font-bold uppercase tracking-widest text-[11px]">
                {cancelling ? "Processing..." : "Confirm Cancellation"}
              </button>
              <button onClick={() => !cancelling && setShowCancelModal(false)} className="w-full bg-gray-100 text-gray-600 py-4 rounded-md font-bold uppercase tracking-widest text-[11px]">Keep Plan</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}